/**
 * Organism: NotificationPanel
 * Slide-in notification panel from top bar bell icon.
 */
import React, { useEffect, useRef } from 'react'
import { tokens } from '@/design-system/tokens'
import { Bell, X, Check, CheckCheck, FileText, DollarSign, ClipboardList, HelpCircle, AlertCircle, Calendar, BarChart2, Stamp } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/atoms/Button'

export interface Notification {
  id: number
  user_id: number
  event_type: string
  title: string
  body: string
  entity_type: string
  entity_id: string | number | null
  read_at: string | null
  created_at: string
}

interface NotificationPanelProps {
  open: boolean
  onClose: () => void
  notifications: Notification[]
  unreadCount: number
  onMarkRead: (id: number) => void
  onMarkAllRead: () => void
  // BUG-NOTIFICATIONS-POPOVER-ITEMS-INERT — activate = navigate to the
  // notification's source entity + mark read + close popover. Parent
  // owns the routing; the panel just delegates the click intent.
  onActivate?: (notif: Notification) => void
}

function eventIcon(eventType: string): React.ReactNode {
  if (eventType.includes('permit')) return <Stamp size={14} />
  if (eventType.includes('invoice')) return <DollarSign size={14} />
  if (eventType.includes('change_order')) return <FileText size={14} />
  if (eventType.includes('rfi')) return <HelpCircle size={14} />
  if (eventType.includes('task')) return <ClipboardList size={14} />
  if (eventType.includes('po')) return <BarChart2 size={14} />
  if (eventType.includes('daily_report')) return <Calendar size={14} />
  if (eventType.includes('project')) return <AlertCircle size={14} />
  return <Bell size={14} />
}

function eventColor(eventType: string): string {
  // PERMITS-REDESIGN-SURFACE-SWEEP — revocation is loud red, others gold
  if (eventType === 'permit_revoked') return 'var(--st-error)'
  if (eventType.includes('permit')) return 'var(--brand-gold)'
  if (eventType.includes('overdue') || eventType.includes('rejected')) return 'var(--st-error)'
  if (eventType.includes('paid') || eventType.includes('approved')) return 'var(--st-active)'
  if (eventType.includes('invoice') || eventType.includes('po')) return 'var(--st-info)'
  if (eventType.includes('change_order')) return 'var(--st-warning)'
  if (eventType.includes('rfi')) return 'var(--st-draft)'
  if (eventType.includes('task')) return 'var(--st-done)'
  return tokens.color.brand.gold
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  open,
  onClose,
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  onActivate,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: 52,
        right: 12,
        width: 380,
        maxHeight: 'calc(100vh - 80px)',
        background: tokens.color.background.surface,
        border: `1px solid ${tokens.color.border.default}`,
        borderRadius: '10px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: tokens.typography.fontFamily.sans,
      }}
    >
      {/* Header */}
      <div style={{
        padding: '14px 16px',
        borderBottom: `1px solid ${tokens.color.border.default}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Bell size={15} color={tokens.color.brand.gold} />
          <span style={{ fontSize: '14px', fontWeight: 600, color: tokens.color.text.primary }}>
            Notifications
          </span>
          {unreadCount > 0 && (
            <span style={{
              background: tokens.color.brand.gold,
              color: 'var(--bg-page)',
              fontSize: '10px',
              fontWeight: 700,
              padding: '1px 6px',
              borderRadius: '9999px',
            }}>
              {unreadCount}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {unreadCount > 0 && (
            <Button
              onClick={onMarkAllRead}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: tokens.color.text.muted,
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
              title="Mark all read"
            >
              <CheckCheck size={12} /> Mark all read
            </Button>
          )}
          <Button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: tokens.color.text.muted, padding: '2px' }}
          >
            <X size={14} />
          </Button>
        </div>
      </div>

      {/* Notifications list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: tokens.color.text.muted, fontSize: '13px' }}>
            <Bell size={28} style={{ opacity: 0.3, marginBottom: '8px' }} />
            <div>No notifications yet</div>
          </div>
        ) : (
          notifications.map(notif => {
            const isUnread = !notif.read_at
            const color = eventColor(notif.event_type)
            const activate = () => {
              if (onActivate) onActivate(notif)
              else if (isUnread) onMarkRead(notif.id)
            }
            return (
              <div
                key={notif.id}
                onClick={activate}
                role="button"
                tabIndex={0}
                aria-label={`Open ${notif.title}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    activate()
                  }
                }}
                onFocus={(e) => {
                  e.currentTarget.style.outline = `2px solid ${tokens.color.brand.gold}`
                  e.currentTarget.style.outlineOffset = '-2px'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.outline = 'none'
                  e.currentTarget.style.outlineOffset = ''
                }}
                style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${tokens.color.border.default}`,
                  background: isUnread ? `${color}08` : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  transition: 'background 0.15s',
                }}
              >
                {/* Icon */}
                <div style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: `${color}22`,
                  color: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '2px',
                }}>
                  {eventIcon(notif.event_type)}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: isUnread ? 600 : 400,
                    color: tokens.color.text.primary,
                    marginBottom: '2px',
                    lineHeight: '1.4',
                  }}>
                    {notif.title}
                  </div>
                  {notif.body && (
                    <div style={{
                      fontSize: '12px',
                      color: tokens.color.text.muted,
                      marginBottom: '4px',
                      lineHeight: '1.4',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {notif.body}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: tokens.color.text.muted }}>
                    {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                  </div>
                </div>

                {/* Unread dot */}
                {isUnread && (
                  <div style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: color,
                    flexShrink: 0,
                    marginTop: '5px',
                  }} />
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
